import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import {
  getRequiredDocumentsCatalog,
  createRequiredDocument,
  updateRequiredDocument,
  deleteRequiredDocument,
} from 'services/adminNegotiationService';
import { formatDate } from 'utils/formatDate';
import { adminNegotiationI18n } from './i18n';
import TextArea from '../../components/TextArea/TextArea';
import AsyncStateView from '../../components/AsyncStateView/AsyncStateView';
import '../../styles/naEeppTableGrid.scss';

/** Lee el valor de un onChange que puede venir como evento (target.value) o valor crudo. */
const readVal = (e) =>
  e && typeof e === 'object' && 'target' in e ? e.target.value : e;

const FILTER_ALL = '__all__';
// Set EXISTENTE de EasyLawyer (sin cambio de contrato): ingresos|deudas|bienes|tributario.
const CATEGORIAS = ['ingresos', 'deudas', 'bienes', 'tributario'];
// Contrato EasyLawyer: 10 condiciones (gating por-cliente en su form; Legal no la interpreta).
const CONDICIONES = [
  'comun',
  'dependiente',
  'independiente',
  'cesante',
  'vehiculo',
  'vivienda',
  'tarjetas',
  'credito_consumo',
  'pension_alimentos',
  'deuda_indirecta',
];
const NAME_MAX = 256;
const URL_MAX = 1024;

const blankDoc = {
  id: null,
  name: '',
  description: '',
  sourceUrl: '',
  categoria: 'ingresos',
  condicion: 'comun',
  isActive: true,
};

/**
 * Catálogo dinámico de documentos requeridos de Negociación Administrativa
 * (AdminNegotiationRequiredDocument_SAAS). CRUD: toda la lista activa se envía a
 * EasyLawyer en cada proceso (no hay selección por-caso). Borrado = soft-delete
 * (IsActive=0) desde el campo Estado del form de edición. Reusa el design system
 * (Table/Modal/Button/IconButton/Badge/Input/Dropdown/Card/Skeleton) + TextArea local.
 */
export default function AdminNegotiationRequiredDocuments({
  DesignSystemComponents,
}) {
  const { t } = useScopedTranslation(adminNegotiationI18n);

  const {
    Button,
    Table,
    Badge,
    IconButton,
    Dropdown,
    Modal,
    Input,
    Card,
    Skeleton,
  } = DesignSystemComponents ?? {};

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null = lista; objeto = form (crear/editar)
  // Estado async del guardado del form (idle/loading/success/error) → body-swap (AsyncStateView).
  const [docStatus, setDocStatus] = useState('idle');
  const [docError, setDocError] = useState(null);
  // Deshabilita los campos del form durante el submit (el body-swap del AsyncStateView ya
  // muestra el loader; mantiene los disabled= coherentes sin un estado extra).
  const saving = docStatus === 'loading';
  // Filtros + paginación client-side (el catálogo llega completo).
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(FILTER_ALL);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const submittingRef = useRef(false);
  const abortRef = useRef(null);
  // Solo para gatear setState post-await en mutaciones: no abortamos el request
  // (un guardado/desactivación debe completar en el server aunque el usuario navegue).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchDocs = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await getRequiredDocumentsCatalog(
        { includeInactive: true },
        controller.signal
      );
      setDocs(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(
        err.response?.data?.message || err.message || t('rd_error_generic')
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchDocs();
    return () => abortRef.current?.abort();
  }, [fetchDocs]);

  const canSubmit = (editing?.name || '').trim().length > 0;

  const handleSave = async () => {
    if (!canSubmit || submittingRef.current) return; // validación pre-submit: inline (idle).
    submittingRef.current = true;
    setDocStatus('loading');
    setDocError(null);
    setError(null);
    try {
      const payload = {
        name: editing.name.trim(),
        description: (editing.description || '').trim() || null,
        sourceUrl: (editing.sourceUrl || '').trim() || null,
        categoria: editing.categoria,
        condicion: editing.condicion,
      };
      if (editing.id) {
        // Sin signal: la mutación debe completar en el server aunque el usuario navegue.
        // Estado (espeja el toggle): Activo → PUT isActive:true (+ campos); Inactivo →
        // PUT de campos + DELETE de soft-delete (persiste las ediciones y desactiva).
        if (editing.isActive) {
          await updateRequiredDocument(editing.id, {
            ...payload,
            isActive: true,
          });
        } else {
          await updateRequiredDocument(editing.id, {
            ...payload,
            isActive: true,
          });
          await deleteRequiredDocument(editing.id);
        }
      } else {
        await createRequiredDocument(payload);
      }
      if (mountedRef.current) setDocStatus('success');
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      if (!mountedRef.current) return;
      setDocError(
        err.response?.data?.message || err.message || t('rd_error_generic')
      );
      setDocStatus('error');
    } finally {
      submittingRef.current = false;
    }
  };

  const statusBadge = (r) => {
    const text = r.isActive ? t('rd_status_active') : t('rd_status_inactive');
    const color = r.isActive ? 'success' : 'light';
    return Badge ? (
      <Badge text={text} color={color} size='sm' />
    ) : (
      <span>{text}</span>
    );
  };

  const excerpt = (value) => {
    if (!value) return '—';
    const clean = String(value).trim();
    return clean.length > 90 ? `${clean.slice(0, 90)}…` : clean;
  };

  const categoriaOptions = useMemo(
    () => CATEGORIAS.map((c) => ({ label: t(`rd_category_${c}`), value: c })),
    [t]
  );

  const condicionOptions = useMemo(
    () => CONDICIONES.map((c) => ({ label: t(`rd_condition_${c}`), value: c })),
    [t]
  );

  // Opciones del Dropdown de Estado (patrón CreateEditCourtModal): booleano directo
  // mapeado al flag `isActive`. Reusa las keys de badge ya existentes.
  const estadoOptions = useMemo(
    () => [
      { label: t('rd_status_active'), value: true },
      { label: t('rd_status_inactive'), value: false },
    ],
    [t]
  );

  const columns = [
    { accessor: 'name', label: t('rd_col_name') },
    {
      accessor: 'description',
      label: t('rd_col_description'),
      render: (r) => (
        <span title={r.description || ''}>{excerpt(r.description)}</span>
      ),
    },
    {
      accessor: 'sourceUrl',
      label: t('rd_col_url'),
      render: (r) =>
        r.sourceUrl ? (
          <a
            href={r.sourceUrl}
            target='_blank'
            rel='noreferrer'
            title={r.sourceUrl}
            style={{ color: 'var(--success-500, #16a34a)', fontWeight: 600 }}
          >
            ✓
          </a>
        ) : (
          '—'
        ),
    },
    {
      accessor: 'categoria',
      label: t('rd_col_category'),
      render: (r) => t(`rd_category_${r.categoria}`) || r.categoria || '—',
    },
    {
      accessor: 'condicion',
      label: t('rd_col_condition'),
      render: (r) => t(`rd_condition_${r.condicion}`) || r.condicion || '—',
    },
    { accessor: 'isActive', label: t('rd_col_status'), render: statusBadge },
    {
      label: t('rd_col_actions'),
      render: (r) => (
        <div className='d-flex gap-2 align-items-center'>
          {IconButton ? (
            <IconButton
              ariaLabel={t('rd_edit')}
              icon='pencil'
              size='sm'
              stroke='var(--primary-500)'
              onClick={() => {
                setDocStatus('idle');
                setDocError(null);
                setEditing({ ...r });
              }}
            />
          ) : (
            <button
              type='button'
              className='btn btn-link btn-sm'
              onClick={() => {
                setDocStatus('idle');
                setDocError(null);
                setEditing({ ...r });
              }}
            >
              {t('rd_edit')}
            </button>
          )}
        </div>
      ),
    },
  ];

  const statusOptions = useMemo(
    () => [
      { label: t('rd_filter_all_status'), value: FILTER_ALL },
      { label: t('rd_status_active'), value: 'active' },
      { label: t('rd_status_inactive'), value: 'inactive' },
    ],
    [t]
  );

  const visibleDocs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return docs.filter((d) => {
      if (q && !`${d.name} ${d.description || ''}`.toLowerCase().includes(q))
        return false;
      if (statusFilter === 'active' && !d.isActive) return false;
      if (statusFilter === 'inactive' && d.isActive) return false;
      return true;
    });
  }, [docs, search, statusFilter]);

  const ContentWrapper = Card || 'div';
  const contentWrapperProps = Card
    ? { specialPadding: 'p-4' }
    : { className: 'container-fluid p-4' };

  const formBody = editing && (
    <AsyncStateView
      status={docStatus}
      loadingTitle={t('rd_state_loading')}
      successTitle={t('rd_state_success')}
      errorMessage={docError}
      onBack={() => setDocStatus('idle')}
      onSuccessAutoClose={() => {
        setEditing(null);
        fetchDocs();
      }}
    >
      <div>
        {error && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}
        <div className='d-flex flex-column gap-3'>
          {Input && (
            <Input
              label={t('rd_field_name')}
              value={editing.name}
              onChange={(e) => setEditing((s) => ({ ...s, name: readVal(e) }))}
              disabled={saving}
              maxLength={NAME_MAX}
              width='100%'
            />
          )}
          <TextArea
            label={t('rd_field_description')}
            value={editing.description}
            onChange={(e) =>
              setEditing((s) => ({ ...s, description: readVal(e) }))
            }
            disabled={saving}
            rows={3}
          />
          {Input && (
            <Input
              label={t('rd_field_url')}
              value={editing.sourceUrl}
              onChange={(e) =>
                setEditing((s) => ({ ...s, sourceUrl: readVal(e) }))
              }
              disabled={saving}
              placeholder='https://…'
              maxLength={URL_MAX}
              width='100%'
            />
          )}
          <div className='d-flex flex-wrap gap-3'>
            {Dropdown && (
              <div style={{ flex: '1 1 200px' }}>
                <label className='caption font-light d-block mb-1'>
                  {t('rd_field_category')}
                </label>
                <Dropdown
                  options={categoriaOptions}
                  value={
                    categoriaOptions.find(
                      (o) => o.value === editing.categoria
                    ) || categoriaOptions[0]
                  }
                  onChange={(o) =>
                    setEditing((s) => ({
                      ...s,
                      categoria: o?.value ?? CATEGORIAS[0],
                    }))
                  }
                  width='100%'
                />
              </div>
            )}
            {Dropdown && (
              <div style={{ flex: '1 1 200px' }}>
                <label className='caption font-light d-block mb-1'>
                  {t('rd_field_condition')}
                </label>
                <Dropdown
                  options={condicionOptions}
                  value={
                    condicionOptions.find(
                      (o) => o.value === editing.condicion
                    ) || condicionOptions[0]
                  }
                  onChange={(o) =>
                    setEditing((s) => ({
                      ...s,
                      condicion: o?.value ?? CONDICIONES[0],
                    }))
                  }
                  width='100%'
                />
              </div>
            )}
          </div>
          {editing.id && Dropdown && (
            <div style={{ maxWidth: 220 }}>
              <label className='form-label'>{t('rd_estado_label')}</label>
              <Dropdown
                options={estadoOptions}
                value={
                  estadoOptions.find((o) => o.value === !!editing.isActive) ??
                  estadoOptions[0]
                }
                onChange={(o) =>
                  setEditing((s) => ({ ...s, isActive: o?.value ?? true }))
                }
                width='100%'
              />
            </div>
          )}
          {editing.id && (editing.createdBy || editing.updatedBy) && (
            <div className='caption font-light'>
              {editing.createdBy && (
                <div>
                  {t('rd_created_by')}:{' '}
                  {(editing.createdByName || '').trim() || editing.createdBy}
                </div>
              )}
              {editing.updatedBy && (
                <div>
                  {t('rd_updated_by')}:{' '}
                  {(editing.updatedByName || '').trim() || editing.updatedBy}
                  {editing.updateAt ? ` · ${formatDate(editing.updateAt)}` : ''}
                </div>
              )}
            </div>
          )}
        </div>
        <div className='d-flex justify-content-end gap-2 mt-3'>
          {Button && (
            <Button
              label={t('rd_cancel')}
              size='sm'
              variant='secondary'
              onClick={() => {
                setEditing(null);
                setError(null);
              }}
              disabled={saving}
            />
          )}
          {Button && (
            <Button
              label={saving ? t('rd_loading') : t('rd_save')}
              size='sm'
              variant='primary'
              onClick={handleSave}
              disabled={!canSubmit || saving}
            />
          )}
        </div>
      </div>
    </AsyncStateView>
  );

  return (
    <div>
      <div className='mb-4 d-flex justify-content-between align-items-start'>
        <div>
          <h2 className='fw-semibold'>{t('rd_title')}</h2>
          <h4 className='fw-normal'>{t('rd_subtitle')}</h4>
        </div>
        <div className='d-flex gap-2 align-items-center'>
          {Button && (
            <Button
              label={t('rd_add')}
              size='sm'
              variant='primary'
              onClick={() => {
                setError(null);
                setDocStatus('idle');
                setDocError(null);
                setEditing({ ...blankDoc });
              }}
            />
          )}
          {IconButton && (
            <IconButton
              ariaLabel={t('rd_refresh')}
              icon='arrowCounterclockwise'
              size='lg'
              iconFill='var(--primary-500)'
              onClick={fetchDocs}
              disabled={loading}
            />
          )}
        </div>
      </div>

      <ContentWrapper {...contentWrapperProps}>
        {error && !editing && (
          <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>
            {error}
          </div>
        )}

        {/* Filtros: búsqueda (nombre/descripción) + estado */}
        <div className='d-flex flex-wrap gap-2 mb-3' style={{ maxWidth: 720 }}>
          {Input && (
            <div style={{ flex: '1 1 240px' }}>
              <Input
                icon='search'
                placeholder={t('rd_search')}
                value={search}
                onChange={(e) => {
                  setSearch(readVal(e));
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
          {Dropdown && (
            <div style={{ flex: '0 1 200px' }}>
              <Dropdown
                options={statusOptions}
                value={
                  statusOptions.find((o) => o.value === statusFilter) ||
                  statusOptions[0]
                }
                onChange={(o) => {
                  setStatusFilter(o?.value ?? FILTER_ALL);
                  setPage(0);
                }}
                width='100%'
              />
            </div>
          )}
        </div>

        <div className='table-container na-eepp-grid-7'>
          {loading && Skeleton ? (
            <Skeleton count={6} height={40} />
          ) : Table ? (
            <Table
              columns={columns}
              data={visibleDocs}
              loading={loading}
              paginator
              paginationMode='client'
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(newSize) => {
                setPageSize(newSize);
                setPage(0);
              }}
              totalItems={visibleDocs.length}
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
              ]}
            />
          ) : (
            <p className='font-light'>{loading ? t('rd_loading') : null}</p>
          )}
          {!loading && visibleDocs.length === 0 && (
            <div className='text-center p-4'>
              <p className='caption font-light'>
                {search.trim() || statusFilter !== FILTER_ALL
                  ? t('rd_search_empty')
                  : t('rd_empty')}
              </p>
            </div>
          )}
        </div>

        {/* Modal crear/editar */}
        {editing &&
          (Modal ? (
            <Modal
              title={editing.id ? t('rd_edit_title') : t('rd_create_title')}
              show
              onClose={() => {
                if (docStatus === 'loading' || docStatus === 'success') return;
                setEditing(null);
                setError(null);
              }}
              primaryBtnTxt={null}
              secondaryBtnTxt={null}
              width='640px'
            >
              {formBody}
            </Modal>
          ) : (
            <div
              role='dialog'
              style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(15,23,42,0.55)',
                zIndex: 9500,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 32,
              }}
              onClick={() => {
                if (docStatus === 'loading' || docStatus === 'success') return;
                setEditing(null);
                setError(null);
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: '#fff',
                  borderRadius: 8,
                  padding: 20,
                  width: 'min(640px, 94vw)',
                }}
              >
                <h5>
                  {editing.id ? t('rd_edit_title') : t('rd_create_title')}
                </h5>
                {formBody}
              </div>
            </div>
          ))}
      </ContentWrapper>
    </div>
  );
}
